import { useEffect, useState } from "react";
import { Layers, Plus, Pencil, Trash2, Loader2, ShieldX, Star, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { mxn } from "@/lib/format";
import { MoneyInput } from "@/components/common/MoneyInput";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = { nombre: "", precio_mensual: 0,
  creditos_incluidos: 0, precio_credito_adicional: 0,
  modulos: "", es_cotizacion: false, destacado: false, desc: "", orden: 99, stripe_price_id: "" };

export default function GestionPlanes() {
  const { user } = useAuth();
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [delPlan, setDelPlan] = useState(null);

  const load = () => api.get("/admin/planes").then((r) => setPlanes(r.data)).catch(() => setError(true)).finally(() => setLoading(false));

  useEffect(() => {
    if (user && user.role !== "super_admin") { setError(true); setLoading(false); return; }
    if (user) load();
  }, [user]);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditId(p.id);
    setForm({ nombre: p.nombre, precio_mensual: p.precio_mensual,
      creditos_incluidos: p.creditos_incluidos ?? 0, precio_credito_adicional: p.precio_credito_adicional ?? 0,
      modulos: (p.modulos || []).join(", "),
      es_cotizacion: !!p.es_cotizacion, destacado: !!p.destacado, desc: p.desc || "", orden: p.orden ?? 99,
      stripe_price_id: p.stripe_price_id || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setBusy(true);
    const payload = {
      nombre: form.nombre.trim(),
      precio_mensual: Number(form.precio_mensual) || 0,
      creditos_incluidos: Number(form.creditos_incluidos) || 0,
      precio_credito_adicional: Number(form.precio_credito_adicional) || 0,
      modulos: form.modulos.split(",").map((m) => m.trim()).filter(Boolean),
      es_cotizacion: form.es_cotizacion, destacado: form.destacado, desc: form.desc, orden: Number(form.orden) || 99,
      stripe_price_id: (form.stripe_price_id || "").trim(),
    };
    try {
      if (editId) await api.put(`/admin/planes/${editId}`, payload);
      else await api.post("/admin/planes", payload);
      toast.success(editId ? "Plan actualizado (se reflejó en facturación de las entidades)" : "Plan creado");
      setOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    const p = delPlan; setDelPlan(null);
    try { await api.delete(`/admin/planes/${p.id}`); toast.success("Plan eliminado"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo borrar"); }
  };

  if (error)
    return (
      <div>
        <PageHeader title="Planes y precios" subtitle="Panel de plataforma" />
        <div className="flex flex-col items-center justify-center gap-3 p-16 text-slate-400">
          <ShieldX className="h-10 w-10" />
          <p className="text-sm">Acceso exclusivo del Super Administrador de la plataforma.</p>
        </div>
      </div>
    );

  return (
    <div data-testid="gestion-planes">
      <PageHeader title="Planes y precios" subtitle="Fuente única de planes — alimenta landing, registro, facturación y límites de uso">
        <Button data-testid="nuevo-plan-btn" size="sm" className="bg-primary hover:bg-sidebar-hover" onClick={openNew}>
          <Plus className="mr-1.5 h-4 w-4" /> Nuevo plan
        </Button>
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="p-6 md:p-8">
          <p className="mb-4 flex items-center gap-1.5 text-xs text-slate-500"><Layers className="h-3.5 w-3.5" /> El precio se diferencia por <b>altas de clientes incluidas</b> (1 alta = 1 cliente nuevo con consulta real de listas); los clientes en cartera y los usuarios son ilimitados en todos los planes. Editar un plan se refleja en la landing, el registro y la facturación de las entidades en ese plan.</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {planes.map((p) => (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`relative flex flex-col rounded-lg border bg-white p-5 shadow-sm ${p.destacado ? "border-primary ring-1 ring-primary/20" : "border-slate-200"}`}>
                {p.destacado && <span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-white"><Star className="h-2.5 w-2.5" /> Destacado</span>}
                <p className="font-heading text-base font-semibold text-slate-900">{p.nombre}</p>
                <p className="text-xs text-slate-400">{p.desc}</p>
                <p className="mt-3 font-heading text-2xl font-bold text-primary tabular-nums">
                  {p.es_cotizacion ? "Cotización" : mxn(p.precio_mensual)}{!p.es_cotizacion && <span className="text-xs font-normal text-slate-400">/mes</span>}
                  {!p.es_cotizacion && (
                    <span className="ml-1.5 whitespace-nowrap align-middle text-xs font-normal text-slate-400"
                          data-testid={`precio-mas-iva-${p.id}`}>+ IVA</span>
                  )}
                </p>
                <ul className="mt-3 flex-1 space-y-1 text-xs text-slate-600">
                  <li className="font-medium text-slate-700">Clientes ilimitados</li>
                  {p.creditos_incluidos === -1
                    ? <li>Altas de clientes ilimitadas</li>
                    : (p.creditos_incluidos != null
                        ? <li>{Number(p.creditos_incluidos).toLocaleString("es-MX")} altas de clientes incluidas/mes{!p.es_cotizacion && p.precio_credito_adicional ? ` (+${mxn(p.precio_credito_adicional)}/alta adicional)` : ""}</li>
                        : null)}
                  <li>Usuarios ilimitados</li>
                </ul>
                <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                  <span className="text-[11px] text-slate-400">{p.entidades} entidad(es)</span>
                  <div className="flex gap-1">
                    <Button data-testid={`edit-plan-${p.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                    <Button data-testid={`delete-plan-${p.id}`} variant="ghost" size="icon" className="h-8 w-8" disabled={p.entidades > 0} onClick={() => setDelPlan(p)}><Trash2 className={`h-4 w-4 ${p.entidades > 0 ? "text-slate-300" : "text-red-500"}`} /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Diálogo crear/editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Editar plan" : "Nuevo plan"}</DialogTitle>
            <DialogDescription>El precio se diferencia por altas de clientes incluidas (1 alta = 1 cliente nuevo con consulta real de listas). Clientes en cartera y usuarios ilimitados.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs">Nombre</Label>
              <Input data-testid="pf-nombre" value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Descripción corta</Label>
              <Input data-testid="pf-desc" value={form.desc} onChange={(e) => setForm((p) => ({ ...p, desc: e.target.value }))} />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <Label className="text-xs">¿Es plan de cotización? (clientes ilimitados, sin precio fijo)</Label>
              <Switch data-testid="pf-cotizacion" checked={form.es_cotizacion} onCheckedChange={(v) => setForm((p) => ({ ...p, es_cotizacion: v }))} />
            </div>
            {!form.es_cotizacion && (
              <div>
                <Label className="mb-1 block text-xs">Precio mensual ($)</Label>
                <MoneyInput data-testid="pf-precio" value={form.precio_mensual} onChange={(v) => setForm((p) => ({ ...p, precio_mensual: v }))} />
              </div>
            )}
            {!form.es_cotizacion && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block text-xs">Altas de clientes incluidas por mes <span className="text-slate-400">(-1 = ilimitadas)</span></Label>
                  <Input data-testid="pf-creditos-incluidos" type="number" min={-1} value={form.creditos_incluidos} onChange={(e) => setForm((p) => ({ ...p, creditos_incluidos: e.target.value }))} />
                </div>
                <div>
                  <Label className="mb-1 block text-xs">Alta de cliente adicional ($/alta)</Label>
                  <MoneyInput data-testid="pf-credito-adicional" value={form.precio_credito_adicional} onChange={(v) => setForm((p) => ({ ...p, precio_credito_adicional: v }))} />
                </div>
                <p className="col-span-2 -mt-1 text-[11px] text-slate-400">1 alta = 1 cliente nuevo con consulta real de listas (SIC/PLD/PUI). Re-consultar un cliente ya escrutado no consume altas.</p>
              </div>
            )}
            <div>
              <Label className="mb-1 block text-xs">Módulos incluidos (separados por coma)</Label>
              <Textarea data-testid="pf-modulos" rows={2} value={form.modulos} onChange={(e) => setForm((p) => ({ ...p, modulos: e.target.value }))} />
            </div>
            <div>
              <Label className="mb-1 block text-xs">Stripe Price ID (cobro recurrente — modo TEST)</Label>
              <Input data-testid="pf-stripe-price" placeholder="price_..." value={form.stripe_price_id} onChange={(e) => setForm((p) => ({ ...p, stripe_price_id: e.target.value }))} />
              <p className="mt-1 text-[11px] text-slate-400">Pega aquí el Price ID recurrente (mensual, MXN) de tu producto en Stripe. Vacío = el plan no se puede contratar por Checkout.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-xs">Orden</Label>
                <Input data-testid="pf-orden" type="number" value={form.orden} onChange={(e) => setForm((p) => ({ ...p, orden: e.target.value }))} />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3">
                <Label className="text-xs">Destacado</Label>
                <Switch data-testid="pf-destacado" checked={form.destacado} onCheckedChange={(v) => setForm((p) => ({ ...p, destacado: v }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="pf-guardar" onClick={save} disabled={busy} className="bg-primary hover:bg-sidebar-hover">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delPlan} onOpenChange={(v) => !v && setDelPlan(null)}>
        <AlertDialogContent data-testid="delete-plan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar plan</AlertDialogTitle>
            <AlertDialogDescription>¿Eliminar el plan "{delPlan?.nombre}"? Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-plan" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
